import { GridServerSettings } from "./settings";
import {
    convertNetworkTypeToTelemetryType,
    GdprLevel,
    OverrideConfigTypeEnum,
    TelemetryEventBase,
    GSModuleName,
    HTTPVerb,
    ResumeType,
    CodecType,
    IPVersion,
    GS_HttpCallResultDef,
    GS_DebugInfoDef,
    GS_ExceptionInfoDef,
    BooleanType,
    NetworkTypeEnum,
    NetworkType,
    ClientTelemetryConfig,
    TelemetryImpl
} from "./dependencies";
/**
 * Event interface for Http events sent for all non-periodic http calls in library
 */
export interface TelemetryHttpEvent {
    url: string;
    verb: string;
    statusCode: string;
    requestStatusCode: string;
    sessionId: string;
    subSessionId: string;
    requestId: string;
    serverId: string;
    callDuration: number;
    response: string;
}

export declare interface GridServer_GameLaunch_Request {
    requestedZoneAddress: string;
    zoneName: string;
    networkSessionId: string;
    sessionId: string;
    subSessionId: string;
    resumeType: ResumeType;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    result: string;
    codec: CodecType;
    ipVersion: IPVersion;
    launchDuration: number;
    networkType: NetworkTypeEnum;
    streamingProfileGuid: string;
    systemInfoGuid: string;
    cmsId: string;
}

export class GridServer_GameLaunch_RequestDef extends TelemetryEventBase<GridServer_GameLaunch_Request> {
    readonly name: string = "GridServer_GameLaunch_Request";
    readonly gdprLevel: GdprLevel = GdprLevel.Functional;
    constructor(info: GridServer_GameLaunch_Request) {
        super(info);
    }
}

/// todo: Need to fetch the schema details from the JSON schema file
/// Importing json file works, but the build fails as the json file is not getting copied into output
const SessionControlTelemetryConfig: ClientTelemetryConfig = {
    clientName: "GFN_GridServer",
    clientId: "93289439828086895",
    eventSchemaVer: "2.1"
};

class TelemetryHandler extends TelemetryImpl {
    private networkType: NetworkTypeEnum = NetworkTypeEnum.UNKNOWN;
    private cmsId: string = "";
    private sessionId: string = "";
    private subSessionId: string = "";
    private exceptionCounts: Map<string, number> = new Map<string, number>();
    private totalExceptionCount: number = 0;

    constructor() {
        super(SessionControlTelemetryConfig);
    }

    public getTelemetryHttpEvent(
        url: string,
        verb: string,
        sessionId?: string
    ): TelemetryHttpEvent {
        return {
            url,
            verb,
            statusCode: "0",
            requestStatusCode: "",
            sessionId: sessionId ?? "",
            subSessionId: this.subSessionId,
            requestId: "",
            serverId: "",
            callDuration: 0,
            response: "None"
        };
    }

    public getGameLaunchRequestEvent(
        serverAddress: string,
        isResume: boolean,
        networkSessionId?: string,
        sessionId?: string
    ): GridServer_GameLaunch_Request {
        return {
            requestedZoneAddress: serverAddress,
            zoneName: "",
            networkSessionId: networkSessionId ?? "",
            sessionId: sessionId ?? "",
            subSessionId: "",
            resumeType: isResume ? ResumeType.MANUAL : ResumeType.NONE,
            overrideConfigType: GridServerSettings.remoteOverrideInfo.type,
            overrideConfigVersion: GridServerSettings.remoteOverrideInfo.version,
            result: "",
            codec: CodecType.UNKNOWN,
            ipVersion: IPVersion.UNKNOWN,
            launchDuration: 0,
            networkType: this.networkType,
            streamingProfileGuid: this.telemetryEventIds.streamingProfileGuid ?? "",
            systemInfoGuid: this.telemetryEventIds.systemInfoGuid ?? "",
            cmsId: String(this.cmsId)
        };
    }

    public sendHttpEvent(event: TelemetryHttpEvent) {
        const httpEvent: GS_HttpCallResultDef = new GS_HttpCallResultDef({
            callDuration: event.callDuration,
            verb: event.verb as HTTPVerb,
            sessionId: event.sessionId,
            subSessionId: event.subSessionId,
            serverId: event.serverId,
            url: event.url,
            overrideConfigType: GridServerSettings.remoteOverrideInfo.type,
            overrideConfigVersion: GridServerSettings.remoteOverrideInfo.version,
            requestStatusCode: event.requestStatusCode,
            requestId: event.requestId,
            networkType: this.networkType,
            statusCode: event.statusCode,
            cmsId: String(this.cmsId),
            moduleName: GSModuleName.GRID_SERVER,
            response: event.response
        });
        this.processEvent(httpEvent);
    }

    public sendGameLaunchRequestEvent(eventData: GridServer_GameLaunch_Request) {
        const event: GridServer_GameLaunch_RequestDef = new GridServer_GameLaunch_RequestDef(
            eventData
        );
        if (!event.parameters.sessionId) {
            event.parameters.sessionId = this.sessionId;
        }

        if (!event.parameters.subSessionId) {
            event.parameters.subSessionId = this.subSessionId;
        }
        event.parameters.systemInfoGuid = this.telemetryEventIds.systemInfoGuid ?? "";
        event.parameters.streamingProfileGuid = this.telemetryEventIds.streamingProfileGuid ?? "";
        this.processEvent(event);
    }

    public sendDebugEvent(
        key1?: string,
        key2?: string,
        key3?: string,
        key4?: string,
        key5?: string
    ) {
        const event: GS_DebugInfoDef = new GS_DebugInfoDef({
            key1: key1 ?? "",
            key2: key2 ?? "",
            key3: key3 ?? "",
            key4: key4 ?? "",
            key5: key5 ?? "",
            moduleName: GSModuleName.GRID_SERVER,
            networkType: this.networkType,
            overrideConfigType: GridServerSettings.remoteOverrideInfo.type,
            overrideConfigVersion: GridServerSettings.remoteOverrideInfo.version,
            cmsId: String(this.cmsId),
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public sendExceptionEvent(
        error: Error | DOMException | undefined,
        msg: string,
        file: string,
        lineno: number,
        colno: number,
        handled: boolean,
        category?: string
    ) {
        if (!this.canSendExceptionEvent(msg)) {
            return;
        }
        if (!category && error) {
            // If there is no explicit category supplied, construct one from error name and msg.
            // We should extend the schema to include these additional details, should we start
            // passing the category. Right now, category is not used.
            category = error.name + ": " + error.message;
        }

        const event = new GS_ExceptionInfoDef({
            filename: file,
            lineno: lineno,
            stacktrace: error?.stack ?? "",
            colno: colno,
            handled: handled ? BooleanType.TRUE : BooleanType.FALSE,
            category: category ?? "",
            message: msg,
            moduleName: GSModuleName.GRID_SERVER,
            sessionId: this.sessionId,
            subSessionId: this.subSessionId
        });
        this.processEvent(event);
    }

    public setSessionId(sessionId: string) {
        this.sessionId = sessionId;
    }

    public setSubSessionId(subSessionId: string) {
        this.subSessionId = subSessionId;
        this.exceptionCounts.clear();
        this.totalExceptionCount = 0;
    }

    public setCmsId(cmsId: string) {
        this.cmsId = cmsId;
    }

    public setNetworkType(network: NetworkType) {
        this.networkType = convertNetworkTypeToTelemetryType(network);
    }

    public resetCache() {
        this.telemetryEventIds = {};
        this.sessionId = "";
        this.subSessionId = "";
        this.cmsId = "";
    }

    private canSendExceptionEvent(message: string) {
        const MAX_EXCEPTIONS_PER_MSG: number = 10;
        const MAX_EXCEPTIONS_TOTAL: number = 50;

        if (this.totalExceptionCount >= MAX_EXCEPTIONS_TOTAL) {
            return false;
        }
        const count = this.exceptionCounts.get(message) ?? 0;
        if (count >= MAX_EXCEPTIONS_PER_MSG) {
            return false;
        }
        this.totalExceptionCount++;
        this.exceptionCounts.set(message, count + 1);
        return true;
    }
}

// Singleton instance which will be made available to the clients from sessioncontrolutils.
export const ScTelemetryHandler: TelemetryHandler = new TelemetryHandler();
